import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Text,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    IconButton,
    HStack,
    TableContainer,
} from "@chakra-ui/react";

// Custom components
import Card from "components/card/Card";
import Icon from "components/icons/Icon";
import CustomPagination from "components/pagination/Pagination";
import CreateExerciseModal from "./CreateExerciseModal";
import EditExerciseModal from "./EditExerciseModal";

// Icons
import { MdAdd, MdSearch, MdEdit, MdDelete, MdPublic, MdLock } from "react-icons/md";

// Services
import { exerciseService, Exercise, GetExercisesParams } from "services/exerciseService";

export default function ExerciseList() {
    const [exercises, setExercises] = useState<Exercise[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [muscleGroupFilter, setMuscleGroupFilter] = useState("");
    const [equipmentFilter, setEquipmentFilter] = useState("");
    const [showPublicOnly, setShowPublicOnly] = useState(false);

    // Modal states
    const {
        isOpen: isCreateOpen,
        onOpen: onCreateOpen,
        onClose: onCreateClose
    } = useDisclosure();

    const {
        isOpen: isEditOpen,
        onOpen: onEditOpen,
        onClose: onEditClose
    } = useDisclosure();

    const toast = useToast();

    // Chakra Color Mode
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = useColorModeValue("gray.600", "gray.400");
    const brandColor = useColorModeValue("brand.500", "brand.400");
    const bg = useColorModeValue("white", "navy.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

    // Exercise categories and options
    const categories = [
        "strength",
        "cardio",
        "flexibility",
        "sports",
        "balance",
        "coordination"
    ];

    const muscleGroups = [
        "chest",
        "back",
        "shoulders",
        "arms",
        "legs",
        "core",
        "full body",
        "glutes"
    ];

    const equipment = [
        "bodyweight",
        "dumbbells",
        "barbell",
        "resistance bands",
        "machines",
        "kettlebells",
        "cables",
        "none"
    ];

    const itemsPerPage = 10;

    // Get badge colors for different categories
    const getCategoryColor = (category: string) => {
        const colors: { [key: string]: string } = {
            strength: "blue",
            cardio: "red",
            flexibility: "green",
            sports: "orange",
            balance: "purple",
            coordination: "teal",
        };
        return colors[category] || "gray";
    };

    const getMuscleGroupColor = (muscleGroup: string) => {
        const colors: { [key: string]: string } = {
            chest: "red",
            back: "blue",
            shoulders: "orange",
            arms: "purple",
            legs: "green",
            core: "yellow",
            "full body": "teal",
            glutes: "pink",
        };
        return colors[muscleGroup] || "gray";
    };

    const getEquipmentColor = (equipment: string) => {
        const colors: { [key: string]: string } = {
            bodyweight: "cyan",
            dumbbells: "blue",
            barbell: "purple",
            kettlebell: "orange",
            resistance_bands: "green",
            machine: "gray",
            cable: "yellow",
            other: "gray",
        };
        return colors[equipment] || "gray";
    };

    // Fetch exercises
    const fetchExercises = async () => {
        try {
            setLoading(true);
            const params: GetExercisesParams = {
                page: pageIndex + 1, // Convert 0-based to 1-based
                limit: itemsPerPage,
                search: searchTerm || undefined,
                category: categoryFilter || undefined,
                muscle_group: muscleGroupFilter || undefined,
                equipment: equipmentFilter || undefined,
                public_only: showPublicOnly || undefined,
            };

            const response = await exerciseService.getExercises(params);
            setExercises(response.exercises);
            setTotalCount(response.total_count);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to fetch exercises",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExercises();
    }, [pageIndex, searchTerm, categoryFilter, muscleGroupFilter, equipmentFilter, showPublicOnly]);

    // Handle search with debounce
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setPageIndex(0);
            fetchExercises();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    // Handle filter changes
    const handleFilterChange = () => {
        setPageIndex(0);
        fetchExercises();
    };

    // Handle exercise deletion
    const handleDeleteExercise = async (exerciseId: string) => {
        try {
            await exerciseService.deleteExercise(exerciseId);
            toast({
                title: "Success",
                description: "Exercise deleted successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            fetchExercises();
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete exercise",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Handle exercise edit
    const handleEditExercise = (exercise: Exercise) => {
        setSelectedExercise(exercise);
        onEditOpen();
    };

    // Handle exercise creation success
    const handleExerciseCreated = () => {
        onCreateClose();
        fetchExercises();
        toast({
            title: "Success",
            description: "Exercise created successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
        });
    };

    // Handle exercise update success
    const handleExerciseUpdated = () => {
        onEditClose();
        setSelectedExercise(null);
        fetchExercises();
        toast({
            title: "Success",
            description: "Exercise updated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
        });
    };

    return (
        <>
            <Card 
                bg={bg} 
                borderRadius="20px"
                boxShadow="0 20px 40px -12px rgba(0,0,0,0.1)"
                border="1px solid"
                borderColor={useColorModeValue("gray.100", "gray.700")}
                overflow="hidden"
                p={{ base: 4, md: 6 }}
            >
                <Flex direction="column" w="100%">
                    {/* Header */}
                    <Flex 
                        justify="space-between" 
                        align={{ base: "start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={{ base: 4, md: 0 }}
                        mb="20px"
                    >
                        <Text color={textColor} fontSize={{ base: "lg", md: "22px" }} fontWeight="700">
                            Exercise Library
                        </Text>
                        <Button
                            leftIcon={<Icon as={MdAdd} />}
                            colorScheme="brand"
                            onClick={onCreateOpen}
                            size={{ base: "md", md: "md" }}
                            w={{ base: "full", md: "auto" }}
                        >
                            Create Exercise
                        </Button>
                    </Flex>

                    {/* Filters */}
                    <Grid
                        templateColumns={{
                            base: "1fr",
                            md: "1fr 1fr",
                            lg: "2fr 1fr 1fr 1fr",
                        }}
                        gap="20px"
                        mb="20px"
                    >
                        <InputGroup>
                            <InputLeftElement pointerEvents="none">
                                <Icon as={MdSearch} color={useColorModeValue("gray.500", "gray.400")} />
                            </InputLeftElement>
                            <Input
                                placeholder="Search exercises..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg={bg}
                                border="1px solid"
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: textColorSecondary }}
                                _focus={{
                                    borderColor: brandColor,
                                    boxShadow: `0 0 0 1px ${brandColor}`,
                                }}
                                _hover={{
                                    borderColor: useColorModeValue("gray.300", "navy.500"),
                                }}
                            />
                        </InputGroup>

                        <Select
                            placeholder="All Categories"
                            value={categoryFilter}
                            onChange={(e) => {
                                setCategoryFilter(e.target.value);
                                handleFilterChange();
                            }}
                            bg={bg}
                            border="1px solid"
                            borderColor={borderColor}
                            color={textColor}
                            _focus={{
                                borderColor: brandColor,
                                boxShadow: `0 0 0 1px ${brandColor}`,
                            }}
                            _hover={{
                                borderColor: useColorModeValue("gray.300", "navy.500"),
                            }}
                        >
                            {categories.map((category) => (
                                <option key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </option>
                            ))}
                        </Select>

                        <Select
                            placeholder="All Muscle Groups"
                            value={muscleGroupFilter}
                            onChange={(e) => {
                                setMuscleGroupFilter(e.target.value);
                                handleFilterChange();
                            }}
                            bg={bg}
                            border="1px solid"
                            borderColor={borderColor}
                            color={textColor}
                            _focus={{
                                borderColor: brandColor,
                                boxShadow: `0 0 0 1px ${brandColor}`,
                            }}
                            _hover={{
                                borderColor: useColorModeValue("gray.300", "navy.500"),
                            }}
                        >
                            {muscleGroups.map((muscle) => (
                                <option key={muscle} value={muscle}>
                                    {muscle.charAt(0).toUpperCase() + muscle.slice(1)}
                                </option>
                            ))}
                        </Select>

                        <Select
                            placeholder="All Equipment"
                            value={equipmentFilter}
                            onChange={(e) => {
                                setEquipmentFilter(e.target.value);
                                handleFilterChange();
                            }}
                            bg={bg}
                            border="1px solid"
                            borderColor={borderColor}
                            color={textColor}
                            _focus={{
                                borderColor: brandColor,
                                boxShadow: `0 0 0 1px ${brandColor}`,
                            }}
                            _hover={{
                                borderColor: useColorModeValue("gray.300", "navy.500"),
                            }}
                        >
                            {equipment.map((equip) => (
                                <option key={equip} value={equip}>
                                    {equip.charAt(0).toUpperCase() + equip.slice(1)}
                                </option>
                            ))}
                        </Select>
                    </Grid>

                    {/* Exercise Table */}
                    {loading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" color={brandColor} />
                        </Flex>
                    ) : exercises.length > 0 ? (
                        <>
                            <TableContainer>
                                <Table variant="simple" color={textColorSecondary} mb="24px" mt="12px">
                                    <Thead>
                                        <Tr>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Name
                                            </Th>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Category
                                            </Th>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Muscle Group
                                            </Th>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Equipment
                                            </Th>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Status
                                            </Th>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Created
                                            </Th>
                                            <Th 
                                                pe="10px" 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                                textAlign="center"
                                            >
                                                Actions
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {exercises.map((exercise) => (
                                            <Tr key={exercise.id} _hover={{ bg: hoverBg }}>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <VStack align="start" spacing={1}>
                                                        <Text
                                                            color={textColor}
                                                            fontWeight="500"
                                                            fontSize="14px"
                                                        >
                                                            {exercise.name}
                                                        </Text>
                                                        {exercise.description && (
                                                            <Text
                                                                color={textColorSecondary}
                                                                fontSize="12px"
                                                                noOfLines={2}
                                                                maxW="200px"
                                                            >
                                                                {exercise.description}
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Badge
                                                        colorScheme={getCategoryColor(exercise.category)}
                                                        fontSize="xs"
                                                        textTransform="uppercase"
                                                        variant="solid"
                                                    >
                                                        {exercise.category}
                                                    </Badge>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Badge
                                                        colorScheme={getMuscleGroupColor(exercise.muscle_group)}
                                                        fontSize="xs"
                                                        textTransform="capitalize"
                                                        variant="solid"
                                                    >
                                                        {exercise.muscle_group}
                                                    </Badge>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    {exercise.equipment && (
                                                        <Badge
                                                            colorScheme={getEquipmentColor(exercise.equipment)}
                                                            fontSize="xs"
                                                            textTransform="capitalize"
                                                            variant="solid"
                                                        >
                                                            {exercise.equipment.replace('_', ' ')}
                                                        </Badge>
                                                    )}
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Badge
                                                        colorScheme={exercise.is_public ? 'green' : 'orange'}
                                                        fontSize="xs"
                                                        variant="solid"
                                                    >
                                                        {exercise.is_public ? "Public" : "Private"}
                                                    </Badge>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Text
                                                        color={textColorSecondary}
                                                        fontSize="sm"
                                                        fontWeight="400"
                                                    >
                                                        {new Date(exercise.created_at).toLocaleDateString()}
                                                    </Text>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <HStack spacing={1} justify="center">
                                                        <IconButton
                                                            aria-label="Edit exercise"
                                                            icon={<Icon as={MdEdit} />}
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleEditExercise(exercise)}
                                                        />
                                                        <IconButton
                                                            aria-label="Delete exercise"
                                                            icon={<Icon as={MdDelete} />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme="red"
                                                            onClick={() => {
                                                                if (window.confirm("Are you sure you want to delete this exercise?")) {
                                                                    handleDeleteExercise(exercise.id);
                                                                }
                                                            }}
                                                        />
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>

                            {/* Custom Pagination */}
                            <CustomPagination 
                                totalResults={totalCount} 
                                pageSize={itemsPerPage}
                                currentPage={pageIndex}
                                onPageChange={(newPageIndex: number) => {
                                    setPageIndex(newPageIndex);
                                }}
                            />
                        </>
                    ) : (
                        <Flex justify="center" align="center" minH="200px">
                            <VStack spacing="20px">
                                <Text color={textColor} fontSize="18px">
                                    No exercises found
                                </Text>
                                <Button
                                    leftIcon={<Icon as={MdAdd} />}
                                    colorScheme="brand"
                                    onClick={onCreateOpen}
                                >
                                    Create First Exercise
                                </Button>
                            </VStack>
                        </Flex>
                    )}
                </Flex>
            </Card>

            {/* Modals */}
            <CreateExerciseModal
                isOpen={isCreateOpen}
                onClose={onCreateClose}
                onSuccess={handleExerciseCreated}
            />

            {selectedExercise && (
                <EditExerciseModal
                    isOpen={isEditOpen}
                    onClose={() => {
                        onEditClose();
                        setSelectedExercise(null);
                    }}
                    exercise={selectedExercise}
                    onSuccess={handleExerciseUpdated}
                />
            )}
        </>
    );
}
